import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import styled from 'styled-components';
import Form from 'react-bootstrap/Form';
import Dropdown from 'react-bootstrap/Dropdown';

import NewModel from '../../utilityButtons/NewModel';
import Models from './Models';

import { DEFAULT_ACTIVE_SIDEBARS, useProject } from '@contexts/Project.context';
import { useAuth } from '@contexts/User.context';
import useSetSearchParams from '@hooks/useSetSearchParams.hook';
import { getAvailableModels, getModel, getModelsDetectedOn } from '@utils/api';
import CreateModelModal from '@components/modal/createModelModal/CreateModelModal';

const DropMenu = styled(Dropdown.Menu)`
	max-height: 55vh;
	min-width: 450px;
	overflow-y: overlay;
	padding-top: 0;
	padding-bottom: 0;
	--bs-dropdown-bg: ${props => props.theme.colors.background};
`;
const DropInner = styled.div`
	position: relative;
`;

const FilterLabel = styled.span`
	display: inline-block;
	color: ${props => props.theme.colors.textColor};
	opacity: 0.9;
	font-size: 1rem;
	font-weight: 600;
	text-transform: capitalize;
	position: relative;
	top: 7px;
	margin-right: 0.5rem;
	margin-bottom: 0.75rem;
`;

const TaskPicker = styled(Form)`
	display: flex;
	flex-direction: row;
	margin-right: 20px;
	flex-grow: 1;
	margin-top: 2px;
	align-items: center;
`;

const DropText = styled.i`
	opacity: 0.8;
	padding-right: 10px;
`;

/**
 * The toolbar in the map editor page.
 * It contains all functionality to interact with the map and the annotations on it on it.
 */
const ModelPicker = () => {
	const setSearchParams = useSetSearchParams();
	const [searchParams] = useSearchParams();
	const [showNewModelModal, setShowNewModelModal] = useState(false);

	const {
		project,
		projectMode,
		pickedTask,
		tasks,
		toolBarVisible,
		isDemo,
		annotationMode,
		dispatch,
	} = useProject();

	const { currentUser, roleAdmin } = useAuth();
	const { uuid: userUuid, active_org_id: orgUuid } = currentUser ?? {};
	const { uuid: projectUuid } = project ?? {};
	const { description: modelName } = pickedTask ?? {};

	const {
		data: tasksData,
		isLoading: tasksLoading,
		refetch: refetchTasks,
	} = useQuery({
		queryKey: ['tasks', userUuid, orgUuid],
		queryFn: () => getAvailableModels(),
		enabled: !!userUuid && !!projectUuid,
		refetchOnWindowFocus: false,
	});

	const { data: modelsDetectedOn, isLoading: modelsDetectedOnLoading } = useQuery({
		queryKey: ['models_detected_on', projectUuid],
		queryFn: () => getModelsDetectedOn(projectUuid),
		enabled: !!projectUuid,
		refetchOnWindowFocus: false,
	});

	//** Dispatch task/model to context */
	const pickTask = task => {
		if (!task) return;

		// Reset the state
		dispatch({ type: 'setPickedModelClasses', payload: null });
		dispatch({ type: 'setDetections', payload: [] });
		dispatch({ type: 'setActiveSidebars', payload: DEFAULT_ACTIVE_SIDEBARS });

		// Set the task
		dispatch({ type: 'setPickedTask', payload: task });
		setSearchParams({ model: task.model_uuid });

		const modelType = task.task_type || task.classes[0].task_type;
		if (modelType) dispatch({ type: 'setModelType', payload: modelType });
	};

	useEffect(() => {
		if (tasksLoading || modelsDetectedOnLoading) return;

		if (tasksData?.length > 0) {
			// Add task_type to each task object because it is not present in the API response
			// We need it like this for now
			const updatedTasks = tasksData.map(task => {
				const taskType = Array.isArray(task.classes) ? task.classes[0].task_type : null;
				return {
					...task,
					task_type: taskType,
				};
			});

			dispatch({ type: 'setTasks', payload: updatedTasks });

			if (!pickedTask) {
				// If the model is in the URL, pick it
				const model = searchParams.get('model');
				const task = updatedTasks.find(task => task.model_uuid === model);

				if (task) {
					pickTask(task);
				} else if ((isDemo && project?.default_demo_model) || roleAdmin) {
					// If it's a demo project, get and pick the default demo model
					const fetchDemoModel = async () => {
						try {
							const model_uuid = project.default_demo_model || model;
							if (!model_uuid) return;

							const demoModel = await getModel(model_uuid);

							// Manually set task_type from first class task_type
							const task_type = demoModel?.classes?.[0]?.task_type;

							if (demoModel && demoModel.model_uuid) {
								pickTask({ ...demoModel, task_type }); // merge in task_type
							}
						} catch (error) {
							console.error('Error fetching default demo model', error);
						}
					};
					fetchDemoModel();
				}
			}
		} else {
			dispatch({ type: 'setTasks', payload: null });
		}
	}, [tasksData, modelsDetectedOn]);

	if (annotationMode) {
		return null;
	}
	return (
		<>
			<TaskPicker>
				<FilterLabel>Model:</FilterLabel>

				<Dropdown drop="up" id="modelDropDown">
					<Dropdown.Toggle
						variant="dark"
						drop="up"
						disabled={!toolBarVisible || !tasks || isDemo}>
						{!tasks ? (
							<DropText> Loading... </DropText>
						) : pickedTask ? (
							<DropText>{modelName}</DropText>
						) : (
							<DropText> Select a Model </DropText>
						)}
					</Dropdown.Toggle>

					<DropMenu variant="dark">
						<DropInner>
							<Dropdown.Item className="p-0 mb-2">
								<NewModel
									addCustomTask={() => {
										setShowNewModelModal(true);
									}}
									customModels={tasks?.filter(task => !task.basemodel)}
								/>
							</Dropdown.Item>

							<Models
								pickTask={pickTask}
								refetchTasks={refetchTasks}
								modelsDetectedOn={modelsDetectedOn}
							/>
						</DropInner>
					</DropMenu>
				</Dropdown>
			</TaskPicker>

			{showNewModelModal && (
				<CreateModelModal
					setShowModal={setShowNewModelModal}
					onSuccess={pickTask}
					projectMode={projectMode}
				/>
			)}
		</>
	);
};
export default ModelPicker;
